import { EwpIcon, CraneIcon, VacuumIcon, CombiIcon, JettaIcon, TrafficTruckIcon } from "./blueprints";
// ─── Shared ───────────────────────────────────────────────────────────────────

const CAB_ITEMS = [  // CoF/WoF and rego labels are checked (with photo) in the Certs section
  "Decals — travel height",
  "Decals — switches labeled",
  "Operators manual",
  "Damage / condition",
  "Rust / corrosion — overall condition of cab",
];

// ─── EWP ─────────────────────────────────────────────────────────────────────

const EWP_CERTS = [
  { label: "CoF/WoF",     id: "cof",          hasExpiry: true },
  { label: "Registration", id: "registration", hasExpiry: true },
  { label: "EWP — 6 monthly cert",           id: "ewp6m",    hasExpiry: true },
  { label: "HV boom — 6 monthly cert",        id: "hvboom6m", hasExpiry: true },
  { label: "HV liner test — annual cert",     id: "hvliner",  hasExpiry: true },
  { label: "Acoustic emission — 2 yearly cert", id: "acoustic", hasExpiry: true },
  { label: "Major inspection — 5 yearly cert", id: "major5y", hasExpiry: true },
];

const EWP_CHASSIS_ITEMS = [
  "Hydraulic oil level & condition",
  "Battery — condition & charge",
  "Hydraulic hosing — leaks",
  "Hydraulic hosing — condition & security",
  "Outriggers / jacks — condition & cleanliness",
  "Jack wear pads",
  "Outrigger wear pads",
  "Cone holder",
  "Handrails — condition, security, compliance",
  "Mudflaps — condition & security",
  "Chassis mounting — torque sealed",
  "Foot pads — condition & security",
  "Wheel chocks — condition & security",
  "Reflectors",
  "Earth stud",
  "Critical bolts — torque sealed",
  "Rust / corrosion — overall condition of deck & chassis",
  "Work lights",
  "PTO and pump",
  "Toolbox condition",
  "Spare battery — condition & security",
];

const EWP_BOOM_ITEMS = [
  "Boom certification sticker",
  "Home stow position indicators",
  "Upper control decals",
  "Lower control decals",
  "Level indicator",
  "Slew bolts — torque sealed",
  "Pins",
  "Critical welds — cracks",
  "Decals / load charts",
  "Boom wear pads",
  "Boom rollers",
  "Charging terminals",
  "Debris flaps / wipers",
  "Overall condition of boom",
];

const EWP_BUCKET_ITEMS = [
  "Bucket & boom rest condition",
  "Liner condition",
  "Bucket access — safe & secure",
  "Bucket & winch covers",
  "Lanyard attach points",
  "Decals / load charts",
  "Overall condition of bucket",
  "Rust / corrosion — bucket",
];

const EWP_FUNCTIONAL_ITEMS = [
  "Jacks & feet",
  "Outriggers & spring locks",
  "Interlock — no boom operation unless jacks set",
  "Interlock — no jack operation unless boom stowed",
  "Boom raise / lower",
  "Boom rotation",
  "Boom extend / retract",
  "Bucket raise / lower",
  "Bucket rotation",
  "Arm rotation",
  "Winch raise / lower",
  "Winch pay in / out",
  "Winch rotation",
  "Bucket tilt / level adjustment",
  "Jib",
  "Worklights",
  "Flashers",
  "Leg lights",
  "Out of stow light",
  "E-pump",
  "Overrides",
  "Auto storage",
  "Cab collision program",
  "Handrail collision program",
  // Added last so answers on sheets in progress stay on their items
  "Deadman switch — upper (platform) controls: fitted & working",
  "Deadman switch — lower (ground) controls: fitted & working",
];

const EWP_SERVICE_A = [
  { label: "Take oil sample",                  key: "oilSample",    hasPhoto: true },
  { label: "Replace hydraulic oil filters",    key: "hydrFilters" },
  { label: "Lube outriggers",                  key: "lubeOut" },
  { label: "Grease jacks",                     key: "greaseJacks" },
  { label: "Grease boom",                      key: "greaseBoom" },
];

const EWP_SERVICE_B = [
  { label: "Take oil sample",                  key: "oilSample",    hasPhoto: true },
  { label: "Replace hydraulic oil filters",    key: "hydrFilters" },
  { label: "Replace hydraulic oil",            key: "hydrOil",      litres: "hydrLitres" },
  { label: "Replace slew oil",                 key: "slewOil",      litres: "slewLitres" },
  { label: "Replace winch motor oil",          key: "winchOil",     litres: "winchLitres" },
  { label: "Lube outriggers",                  key: "lubeOut" },
  { label: "Grease jacks",                     key: "greaseJacks" },
  { label: "Grease boom",                      key: "greaseBoom" },
];

// ─── CRANE ────────────────────────────────────────────────────────────────────

const CRANE_CERTS = [
  { label: "CoF/WoF",     id: "cof",          hasExpiry: true },
  { label: "Registration", id: "registration", hasExpiry: true },
  { label: "Crane cert",  id: "crane",        hasExpiry: true },
];

const CRANE_CHASSIS_ITEMS = [
  "Hydraulic hosing — leaks",
  "Hydraulic hosing — condition & security",
  "Outriggers / jacks — condition & cleanliness",
  "Mudflaps — condition & security",
  "Chassis mounting — torque sealed",
  "Foot pads — condition & security",
  "Wheel chocks — condition & security",
  "Reflectors",
  "Cone holder",
  "Handrails — condition, security, compliance",
  "Earth stud",
  "Toolbox condition",
  "Auger & tamper — condition",
  "Rust / corrosion — overall condition of deck & chassis",
  "Work lights",
  "PTO and pump",
];

const CRANE_CRANE_ITEMS = [
  "Certification sticker",
  "Home stow position indicators",
  "Control decals — upper & lower",
  "Critical fasteners / pins",
  "Critical welds — cracks",
  "Boom rest condition",
  "Condition of remote",
  "Decals / load charts",
  "Level indicator",
  "Lower controls",
  "Leg controls",
  "Leg lights",
  "Wear pads",
  "Hydraulic oil level & condition",
  "Rust / corrosion — overall condition of crane",
];

const CRANE_CAB_ITEMS = [
  ...CAB_ITEMS,
  "Spare remote battery — condition & security",
];

const CRANE_FUNCTIONAL_ITEMS = [
  "Jacks & feet",
  "Outriggers & spring locks",
  "Boom raise / lower",
  "Boom extend / retract",
  "Crane rotation",
  "Auxiliary reel operation",
  "Flashers",
  "Overrides",
];

const CRANE_SERVICE_A = [
  { label: "Take oil sample",               key: "oilSample",  hasPhoto: true },
  { label: "Replace hydraulic oil filters", key: "hydrFilters" },
  { label: "Lube outriggers",               key: "lubeOut" },
  { label: "Grease jacks",                  key: "greaseJacks" },
  { label: "Grease boom",                   key: "greaseBoom" },
];

const CRANE_SERVICE_B = [
  { label: "Take oil sample",               key: "oilSample",  hasPhoto: true },
  { label: "Replace hydraulic oil filters", key: "hydrFilters" },
  { label: "Replace hydraulic oil",         key: "hydrOil",    litres: "hydrLitres" },
  { label: "Lube outriggers",               key: "lubeOut" },
  { label: "Grease jacks",                  key: "greaseJacks" },
  { label: "Grease boom",                   key: "greaseBoom" },
];

// ─── VACUUM ───────────────────────────────────────────────────────────────────

const VAC_CERTS = [
  { label: "CoF/WoF",     id: "cof",          hasExpiry: true },
  { label: "Registration", id: "registration", hasExpiry: true },
];

const VAC_CHASSIS_ITEMS = [
  "Hydraulic hosing — leaks",
  "Hydraulic hosing — condition & security",
  "Mudflaps — condition & security",
  "Chassis mounting — torque sealed",
  "Wheel chocks — condition & security",
  "Reflectors",
  "Cone holder",
  "Handrails — condition, security, compliance",
  "Toolbox condition",
  "PTO and pump",
  "Decals / load charts",
  "Lower control decals",
  "Level indicator",
  "Rust / corrosion — overall condition of deck & chassis",
  "Work lights & flashers",
  // Moved from functional
  "Vacuum oil level & condition",
  "Primary shutoff — condition",
];

const VAC_FUNCTIONAL_ITEMS = [
  "Rear door open / close",
  "Rear door locks",
  "Rear door alarms",
  "Door seal",
  "Sight glasses",
  "Secondary shutoff & cyclone",
  "Cam locks & gate valves",
  "Vacuum & pressure relief",
  "Tank raise / lower",
  "Unloader",
  "Hose reel",
  "Lance",
  "Water pump operation",
  "Safety relief",
  "3-way valve operation",
  "Pressure relief",
  "Oil condition",
  "Operating temp",
  "Gauges",
  "Diesel flush",
  "Hydraulic oil level & condition",
];

const VAC_SERVICE_A = [
  { label: "Take oil sample",                       key: "oilSample",   hasPhoto: true },
  { label: "Replace hydraulic oil filters",         key: "hydrFilters" },
  { label: "Replace water pump oil",                key: "waterPumpOil", litres: "waterPumpLitres" },
  { label: "Replace blower oil (tri-lobe only)", customerLabel: "Blower oil changed (if applicable)",    key: "blowerOil",    litres: "blowerLitres" },
  { label: "Inspect & clean final filter",          key: "finalFilter",  condition: "finalCondition" },
  { label: "Check final filter seals",                key: "finalFilterSeals" },
  { label: "Inspect & clean water filter",          key: "waterFilter",  condition: "waterCondition" },
  { label: "Full grease on superstructure",         key: "greaseFull" },
];

const VAC_SERVICE_B = [
  { label: "Take oil sample",                       key: "oilSample",   hasPhoto: true },
  { label: "Replace hydraulic oil filters",         key: "hydrFilters" },
  { label: "Replace hydraulic oil",                 key: "hydrOil",      litres: "hydrLitres" },
  { label: "Replace water pump oil",                key: "waterPumpOil", litres: "waterPumpLitres" },
  { label: "Replace blower oil (tri-lobe only)", customerLabel: "Blower oil changed (if applicable)",    key: "blowerOil",    litres: "blowerLitres" },
  { label: "Inspect & clean final filter",          key: "finalFilter",  condition: "finalCondition" },
  { label: "Check final filter seals",                key: "finalFilterSeals" },
  { label: "Inspect & clean water filter",          key: "waterFilter",  condition: "waterCondition" },
  { label: "Full grease on superstructure",         key: "greaseFull" },
];

// ─── HYDROVAC ─────────────────────────────────────────────────────────────────

const HV_CHASSIS_ITEMS = [
  "Hydraulic hosing — leaks",
  "Hydraulic hosing — condition & security",
  "Mudflaps — condition & security",
  "Chassis mounting — torque sealed",
  "Wheel chocks — condition & security",
  "Reflectors",
  "Cone holder",
  "Handrails — condition, security, compliance",
  "Toolbox condition",
  "PTO and pump",
  "Decals / load charts",
  "Lower control decals",
  "Level indicator",
  "Rust / corrosion — overall condition of deck & chassis",
  "Work lights & flashers",
  "Vacuum oil level & condition",
  "Primary shutoff — condition",
];

const HV_FUNCTIONAL_ITEMS = [
  "Rear door open / close",
  "Rear door locks",
  "Rear door alarms",
  "Door seal",
  "Sight glasses",
  "Secondary shutoff & cyclone",
  "Cam locks & gate valves",
  "Vacuum & pressure relief",
  "Tank raise / lower",
  "Unloader",
  "Suction boom — raise / lower",
  "Suction boom — rotation",
  "Suction boom — extend / retract",
  "Pivot pins and mounting bolts",
  "Main suction valve",
  "Hose condition",
  "Hose reel",
  "Lance",
  "Water pump operation",
  "Safety relief",
  "3-way valve operation",
  "Pressure relief",
  "Oil condition",
  "Operating temp",
  "Gauges",
  "Diesel flush",
  "Hydraulic oil level & condition",
];

// Hydrovac uses same service actions as vacuum
const HV_SERVICE_A = VAC_SERVICE_A;
const HV_SERVICE_B = VAC_SERVICE_B;

// ─── Master config ────────────────────────────────────────────────────────────

export const CONFIGS = {
  "ewp-a": {
    label: "EWP — A-Service", equipment: "EWP", service: "A",
    equipmentFields: ["make", "model", "serial"],
    vehicleFields: ["rego", "pto", "nextsvc"],
    certs: EWP_CERTS,
    sections: [
      { id: "cab",        title: "Visual Inspection — Cab",                items: CAB_ITEMS },
      { id: "chassis",    title: "Visual Inspection — Chassis & Subframe",  items: EWP_CHASSIS_ITEMS },
      { id: "boom",       title: "Visual Inspection — Boom",                items: EWP_BOOM_ITEMS },
      { id: "bucket",     title: "Visual Inspection — Bucket",              items: EWP_BUCKET_ITEMS },
      { id: "functional", title: "Functional Test",                         items: EWP_FUNCTIONAL_ITEMS },
    ],
    serviceActions: EWP_SERVICE_A,
  },
  "ewp-b": {
    label: "EWP — B-Service", equipment: "EWP", service: "B",
    equipmentFields: ["make", "model", "serial"],
    vehicleFields: ["rego", "pto", "nextsvc"],
    certs: EWP_CERTS,
    sections: [
      { id: "cab",        title: "Visual Inspection — Cab",                items: CAB_ITEMS },
      { id: "chassis",    title: "Visual Inspection — Chassis & Subframe",  items: EWP_CHASSIS_ITEMS },
      { id: "boom",       title: "Visual Inspection — Boom",                items: EWP_BOOM_ITEMS },
      { id: "bucket",     title: "Visual Inspection — Bucket",              items: EWP_BUCKET_ITEMS },
      { id: "functional", title: "Functional Test",                         items: EWP_FUNCTIONAL_ITEMS },
    ],
    serviceActions: EWP_SERVICE_B,
  },
  "crane-a": {
    label: "Crane — A-Service", equipment: "Crane", service: "A",
    equipmentFields: ["make", "model", "serial"],
    vehicleFields: ["rego", "pto", "nextsvc"],
    certs: CRANE_CERTS,
    sections: [
      { id: "cab",        title: "Visual Inspection — Cab",        items: CRANE_CAB_ITEMS },
      { id: "chassis",    title: "Visual Inspection — Chassis & Deck", items: CRANE_CHASSIS_ITEMS },
      { id: "crane",      title: "Visual Inspection — Crane",      items: CRANE_CRANE_ITEMS },
      { id: "functional", title: "Functional Test",                items: CRANE_FUNCTIONAL_ITEMS },
    ],
    serviceActions: CRANE_SERVICE_A,
  },
  "crane-b": {
    label: "Crane — B-Service", equipment: "Crane", service: "B",
    equipmentFields: ["make", "model", "serial"],
    vehicleFields: ["rego", "pto", "nextsvc"],
    certs: CRANE_CERTS,
    sections: [
      { id: "cab",        title: "Visual Inspection — Cab",        items: CRANE_CAB_ITEMS },
      { id: "chassis",    title: "Visual Inspection — Chassis & Deck", items: CRANE_CHASSIS_ITEMS },
      { id: "crane",      title: "Visual Inspection — Crane",      items: CRANE_CRANE_ITEMS },
      { id: "functional", title: "Functional Test",                items: CRANE_FUNCTIONAL_ITEMS },
    ],
    serviceActions: CRANE_SERVICE_B,
  },
  "vacuum-a": {
    label: "Vacuum — A-Service", equipment: "Vacuum", service: "A",
    equipmentFields: ["tankerModel", "tankerSerial"],
    vehicleFields: ["rego", "pto", "nextsvc"],
    certs: VAC_CERTS,
    sections: [
      { id: "cab",        title: "Visual Inspection — Cab",            items: CAB_ITEMS },
      { id: "chassis",    title: "Visual Inspection — Chassis & Deck", items: VAC_CHASSIS_ITEMS },
      { id: "functional", title: "Functional Test",                    items: VAC_FUNCTIONAL_ITEMS },
    ],
    operationalChecks: true,
    serviceActions: VAC_SERVICE_A,
  },
  "vacuum-b": {
    label: "Vacuum — B-Service", equipment: "Vacuum", service: "B",
    equipmentFields: ["tankerModel", "tankerSerial"],
    vehicleFields: ["rego", "pto", "nextsvc"],
    certs: VAC_CERTS,
    sections: [
      { id: "cab",        title: "Visual Inspection — Cab",            items: CAB_ITEMS },
      { id: "chassis",    title: "Visual Inspection — Chassis & Deck", items: VAC_CHASSIS_ITEMS },
      { id: "functional", title: "Functional Test",                    items: VAC_FUNCTIONAL_ITEMS },
    ],
    operationalChecks: true,
    serviceActions: VAC_SERVICE_B,
  },
  "hydrovac-a": {
    label: "Hydrovac — A-Service", equipment: "Hydrovac", service: "A",
    equipmentFields: ["tankerModel", "tankerSerial"],
    vehicleFields: ["rego", "pto", "nextsvc"],
    certs: VAC_CERTS,
    sections: [
      { id: "cab",        title: "Visual Inspection — Cab",            items: CAB_ITEMS },
      { id: "chassis",    title: "Visual Inspection — Chassis & Deck", items: HV_CHASSIS_ITEMS },
      { id: "functional", title: "Functional Test",                    items: HV_FUNCTIONAL_ITEMS },
    ],
    operationalChecks: true,
    serviceActions: HV_SERVICE_A,
  },
  "hydrovac-b": {
    label: "Hydrovac — B-Service", equipment: "Hydrovac", service: "B",
    equipmentFields: ["tankerModel", "tankerSerial"],
    vehicleFields: ["rego", "pto", "nextsvc"],
    certs: VAC_CERTS,
    sections: [
      { id: "cab",        title: "Visual Inspection — Cab",            items: CAB_ITEMS },
      { id: "chassis",    title: "Visual Inspection — Chassis & Deck", items: HV_CHASSIS_ITEMS },
      { id: "functional", title: "Functional Test",                    items: HV_FUNCTIONAL_ITEMS },
    ],
    operationalChecks: true,
    serviceActions: HV_SERVICE_B,
  },

  "traffic-truck-a": {
    label: "Traffic Truck — A-Service",
    equipment: "Traffic Truck",
    service: "A",
    equipmentFields: ["make", "model", "serial"],
    vehicleFields: ["vmake", "rego", "odo", "vsvc"],
    certs: [
      { label: "CoF/WoF current",                  id: "cof",          hasExpiry: true },
      { label: "Registration current",             id: "registration", hasExpiry: true },
      { label: "Road user charges (RUC) current", id: "ruc",    hasExpiry: true },
      { label: "Attenuator compliance cert",      id: "attcert",hasExpiry: true },
    ],
    sections: [
      {
        id: "cab",
        title: "Visual Inspection — Cab",
        items: [
          "TM signage & decals — condition & security",
          "Operators manual present",
          "Damage / condition of cab",
          "Rust / corrosion — overall cab condition",
          "Warning lights functional",
          "Beacon / arrow board controls operational",
        ],
      },
      {
        id: "attenuator",
        title: "Visual Inspection — Attenuator",
        items: [
          "Side panels — condition & security",
          "Rear panels — condition & security",
          "Mounting frame — condition, cracks, welds",
          "Mounting bolts — torque sealed",
          "Hydraulic hoses — leaks & condition",
          "Grease points — condition & access",
          "Hydraulic fluid level",
          "Overall condition — crash damage / deformation",
        ],
      },
      {
        id: "tm",
        title: "Visual Inspection — TM Equipment",
        items: [
          "Arrow board — condition & security",
          "Arrow board — LED function all cells",
          "Beacon lights — condition & function",
          "Steps & access — condition & security",
          "Handrails — condition & security",
          "Harness attach points — condition & certification",
          "Latching mechanisms — operation & condition",
          "Signage — condition & security",
          "Toolbox — condition & security",
          "Reflectors & conspicuity tape",
        ],
      },
      {
        id: "functional",
        title: "Functional Test",
        items: [
          "Attenuator deploy — raise / lower",
          "Attenuator lateral movement",
          "Attenuator — hydraulic operation smooth, no cavitation",
          "Arrow board deploy & retract",
          "Arrow board — all display modes",
          "Beacons — all flash patterns",
          "Emergency / override controls functional",
        ],
      },
    ],
    serviceActions: [
      { label: "Grease attenuator superstructure — all zerk fittings", key: "grease" },
      { label: "Check & top up hydraulic fluid",                        key: "hydrCheck" },
    ],
  },

  "traffic-truck-b": {
    label: "Traffic Truck — B-Service",
    equipment: "Traffic Truck",
    service: "B",
    equipmentFields: ["make", "model", "serial"],
    vehicleFields: ["vmake", "rego", "odo", "vsvc"],
    certs: [
      { label: "CoF/WoF current",                  id: "cof",          hasExpiry: true },
      { label: "Registration current",             id: "registration", hasExpiry: true },
      { label: "Road user charges (RUC) current", id: "ruc",    hasExpiry: true },
      { label: "Attenuator compliance cert",      id: "attcert",hasExpiry: true },
    ],
    sections: [
      {
        id: "cab",
        title: "Visual Inspection — Cab",
        items: [
          "TM signage & decals — condition & security",
          "Operators manual present",
          "Damage / condition of cab",
          "Rust / corrosion — overall cab condition",
          "Warning lights functional",
          "Beacon / arrow board controls operational",
        ],
      },
      {
        id: "attenuator",
        title: "Visual Inspection — Attenuator",
        items: [
          "Side panels — condition & security",
          "Rear panels — condition & security",
          "Mounting frame — condition, cracks, welds",
          "Mounting bolts — torque sealed",
          "Hydraulic hoses — leaks & condition",
          "Grease points — condition & access",
          "Hydraulic fluid level",
          "Overall condition — crash damage / deformation",
        ],
      },
      {
        id: "tm",
        title: "Visual Inspection — TM Equipment",
        items: [
          "Arrow board — condition & security",
          "Arrow board — LED function all cells",
          "Beacon lights — condition & function",
          "Steps & access — condition & security",
          "Handrails — condition & security",
          "Harness attach points — condition & certification",
          "Latching mechanisms — operation & condition",
          "Signage — condition & security",
          "Toolbox — condition & security",
          "Reflectors & conspicuity tape",
        ],
      },
      {
        id: "functional",
        title: "Functional Test",
        items: [
          "Attenuator deploy — raise / lower",
          "Attenuator lateral movement",
          "Attenuator — hydraulic operation smooth, no cavitation",
          "Arrow board deploy & retract",
          "Arrow board — all display modes",
          "Beacons — all flash patterns",
          "Emergency / override controls functional",
        ],
      },
    ],
    serviceActions: [
      { label: "Grease attenuator superstructure — all zerk fittings", key: "grease" },
      { label: "Change hydraulic fluid",                                key: "hydrOil", litres: "hydrLitres" },
    ],
  },
};

// ─── Model-specific service action overrides ─────────────────────────────────
// "Vacuum" is the single top-level tile a technician picks — but AV850 and
// AV6500/8000/10000 use the OLD "Vacuum" inspection checksheet, while AV2000H
// and AV7000H/4000H/3500H use the OLD "Hydrovac" inspection checksheet.
// The `template` field on each model tells the app which underlying CONFIGS
// entry (vacuum-a/b vs hydrovac-a/b) actually supplies the visual inspection
// sections; the service-actions list is looked up separately by model id.
// "" / unset model id = generic list, template = the equipment type's own default.

export const MODEL_OPTIONS = {
  vacuum: [
    { id: "",        label: "Generic / not listed",       template: "vacuum" },
    { id: "av850",   label: "AV850",                       template: "vacuum",   exact: true },
    { id: "av2000",  label: "AV2000H",                     template: "hydrovac", exact: true },
    { id: "av7000",  label: "AV7000H",                     template: "hydrovac", exact: true },
    { id: "av4000",  label: "AV4000H",                     template: "hydrovac", exact: true, same: "av7000" },
    { id: "av3500",  label: "AV3500H",                     template: "hydrovac", exact: true, same: "av7000" },
    { id: "av6500",  label: "AV6500",                      template: "vacuum",   exact: true },
    { id: "av8000",  label: "AV8000",                      template: "vacuum",   exact: true, same: "av6500" },
    { id: "av10000", label: "AV10000",                      template: "vacuum",   exact: true },
  ],
  crane: [
    { id: "",        label: "Generic / not listed", template: "crane" },
    { id: "pk19",    label: "PK19",                   template: "crane", exact: true, same: "pk" },
    { id: "pk26",    label: "PK26",                   template: "crane", exact: true, same: "pk" },
    { id: "pk36",    label: "PK36",                   template: "crane", exact: true, same: "pk" },
    { id: "pk",      label: "PK19 / 26 / 36",         template: "crane", hidden: true }, // older saved sheets only
  ],
  ewp: [
    { id: "",          label: "Generic / not listed", template: "ewp" },
    { id: "versalift",  label: "Versalift",            template: "ewp", make: "Versalift" },
    { id: "donghae",    label: "Donghae",               template: "ewp", make: "Donghae" },
  ],
};

// A model that shares its checklists with another (e.g. AV4000H uses the
// AV7000H lists) resolves to that model's id for actions/sections lookups.
export function canonicalModelId(modelId) {
  if (!modelId) return modelId;
  for (const opts of Object.values(MODEL_OPTIONS)) {
    const m = opts.find(o => o.id === modelId);
    if (m) return m.same || m.id;
  }
  return modelId;
}
export function findModelOption(modelId) {
  if (!modelId) return null;
  for (const opts of Object.values(MODEL_OPTIONS)) { const m = opts.find(o => o.id === modelId); if (m) return m; }
  return null;
}

// Given the top-level equipment type and the chosen model id, returns which
// CONFIGS template ("vacuum" | "hydrovac" | "crane" | "ewp" | the equipment
// type itself for types with no model split) supplies the checksheet.
export function resolveTemplate(equipTypeId, modelId) {
  const opts = MODEL_OPTIONS[equipTypeId];
  if (!opts) return equipTypeId; // combi / jetta / traffic-truck — no split, template = itself
  const found = opts.find(m => m.id === (modelId || ""));
  return (found && found.template) || equipTypeId;
}

// AV-series (shared by Vacuum and Hydrovac — same underlying servicing)
const AV7000_A = [
  { label: "Take oil sample from main hydraulic tank",        key: "oilSample", hasPhoto: true },
  { label: "Replace return filter (upper tower)",             key: "returnFilter" },
  { label: "Replace booster filter (bottom of tower)",        key: "boosterFilter" },
  { label: "Inspect & clean water filter",                    key: "waterFilter", condition: "waterCondition" },
  { label: "Inspect & clean final filter",                    key: "finalFilter", condition: "finalCondition" },
  { label: "Check final filter seals",                key: "finalFilterSeals" },
  { label: "Grease equipment",                                key: "grease" },
  { label: "Drain & replace water-pump oil (water blaster — small pump) — 15W-40 to mid sight glass", customerLabel: "Water pump oil changed", key: "waterPumpOil", litres: "waterPumpLitres" },
];
const AV7000_B = [
  ...AV7000_A,
  { label: "Drain & replace blower oil, drive + non-drive side (back cover off tower) — ISO220 to mid sight glass", customerLabel: "Blower oil changed", key: "blowerOil", litres: "blowerLitres" },
  { label: "Drain & replace hydraulic oil — ISO46 to 80% of sight gauge", customerLabel: "Hydraulic oil changed", key: "hydrOil", litres: "hydrLitres" },
];

const AV2000_A = [
  { label: "Replace engine oil filter",                       key: "engineOilFilter" },
  { label: "Drain & replace engine oil — 15W-40, fill to just under 'full' on the dipstick", customerLabel: "Engine oil changed", key: "engineOil", litres: "engineOilLitres" },
  { label: "Dust out air filter (do not replace)", customerLabel: "Air filter cleaned",             key: "dustAirFilter" },
  { label: "Inspect & clean water filter",                    key: "waterFilter", condition: "waterCondition" },
  { label: "Inspect & clean final filter",                    key: "finalFilter", condition: "finalCondition" },
  { label: "Check final filter seals",                key: "finalFilterSeals" },
  { label: "Grease equipment",                                key: "grease" },
  { label: "Drain & replace water-pump oil (water blaster — small pump) — 15W-40 to mid sight glass", customerLabel: "Water pump oil changed", key: "waterPumpOil", litres: "waterPumpLitres" },
];
const AV2000_B = [
  ...AV2000_A.filter(item => item.key !== "dustAirFilter"),
  { label: "Replace diesel (fuel) filter", customerLabel: "Fuel filter replaced",                    key: "fuelFilter" },
  { label: "Replace engine air filter", customerLabel: "Air filter replaced",                       key: "airFilter" },
  { label: "Drain & replace blower oil, drive + non-drive side — ISO220 to mid sight glass", customerLabel: "Blower oil changed", key: "blowerOil", litres: "blowerLitres" },
];

const AV6500_A = [
  { label: "Take oil sample from main hydraulic tank",        key: "oilSample", hasPhoto: true },
  { label: "Replace return filter (top of tank)",              key: "returnFilter" },
  { label: "(If present) replace pressure filter under chassis", customerLabel: "Pressure filter replaced (if fitted)", key: "pressureFilter" },
  { label: "Inspect & clean water filter",                    key: "waterFilter", condition: "waterCondition" },
  { label: "Inspect & clean final filter",                    key: "finalFilter", condition: "finalCondition" },
  { label: "Check final filter seals",                key: "finalFilterSeals" },
  { label: "Grease equipment",                                key: "grease" },
  { label: "If present: drain & replace water-pump oil (water blaster — small pump) — 15W-40 to mid sight glass", customerLabel: "Water pump oil changed (if fitted)", key: "waterPumpOil", litres: "waterPumpLitres" },
];
const AV6500_B = [
  ...AV6500_A,
  { label: "Drain & replace hydraulic oil — ISO46 to 80% of sight gauge", customerLabel: "Hydraulic oil changed", key: "hydrOil", litres: "hydrLitres" },
];

const AV10000_A = [
  { label: "Take oil sample from main hydraulic tank",        key: "oilSample", hasPhoto: true },
  { label: "Replace return filter (top of tank)",              key: "returnFilter" },
  { label: "(If present) replace pressure filter under chassis", customerLabel: "Pressure filter replaced (if fitted)", key: "pressureFilter" },
  { label: "Inspect & clean water filter",                    key: "waterFilter", condition: "waterCondition" },
  { label: "Inspect & clean final filter",                    key: "finalFilter", condition: "finalCondition" },
  { label: "Check final filter seals",                key: "finalFilterSeals" },
  { label: "Grease equipment",                                key: "grease" },
  { label: "Drain & replace water-pump oil (water blaster — small pump) — 15W-40 to mid sight glass", customerLabel: "Water pump oil changed", key: "waterPumpOil", litres: "waterPumpLitres" },
];
const AV10000_B = [
  ...AV10000_A,
  { label: "Drain & replace blower oil, both sides (bungs accessed through bottom plate) — ISO220 to mid sight glass", customerLabel: "Blower oil changed", key: "blowerOil", litres: "blowerLitres" },
  { label: "Drain & replace hydraulic oil — ISO46 to 80% of sight gauge", customerLabel: "Hydraulic oil changed", key: "hydrOil", litres: "hydrLitres" },
];

const AV8000C_A = [
  { label: "Take oil sample from main hydraulic tank",        key: "oilSample", hasPhoto: true },
  { label: "Replace return filter (upper tower)",              key: "returnFilter" },
  { label: "Replace booster filter (bottom of tower, passenger side)", key: "boosterFilterP" },
  { label: "Replace booster filter (behind tower, driver's side)", key: "boosterFilterD" },
  { label: "Inspect & clean water filter",                    key: "waterFilter", condition: "waterCondition" },
  { label: "Inspect & clean final filter",                    key: "finalFilter", condition: "finalCondition" },
  { label: "Check final filter seals",                key: "finalFilterSeals" },
  { label: "Grease equipment",                                key: "grease" },
  { label: "Drain & replace water blaster pump oil (small pump) — 15W-40 to mid sight glass", customerLabel: "Water blaster pump oil changed", key: "waterBlasterOil", litres: "waterBlasterLitres" },
];
const AV8000C_B = [
  ...AV8000C_A,
  { label: "Drain & replace blower oil, drive + non-drive side (back cover off tower) — ISO220 to mid sight glass", customerLabel: "Blower oil changed", key: "blowerOil", litres: "blowerLitres" },
  { label: "Drain & replace jetter pump oil (large pump running the jetting/hose reel system — annual) — 15W-40", customerLabel: "Jetter pump oil changed", key: "jetterOil", litres: "jetterLitres" },
  { label: "Drain & replace hydraulic oil — ISO46 to 80% of sight gauge", customerLabel: "Hydraulic oil changed", key: "hydrOil", litres: "hydrLitres" },
];

// Crane models
const PK_A = [
  { label: "Take oil sample from main hydraulic tank",        key: "oilSample", hasPhoto: true },
  { label: "Replace return filter (top of hyd tank)",          key: "returnFilter" },
  { label: "Replace pressure filter (bottom of crane)",        key: "pressureFilter" },
  { label: "Grease every extension of the crane (spray lube) + outrigger slides", customerLabel: "Crane extensions & outriggers greased", key: "greaseCrane" },
];
const PK_B = [
  ...PK_A,
  { label: "Drain & replace hydraulic oil — ISO46 to 90% of sight gauge", customerLabel: "Hydraulic oil changed", key: "hydrOil", litres: "hydrLitres" },
];

// EWP models
const VERSALIFT_A = [
  { label: "Take oil sample from main hydraulic tank",        key: "oilSample", hasPhoto: true },
  { label: "Replace suction filter (inside pedestal above hyd tank)", key: "suctionFilter" },
  { label: "Replace pressure filter (underneath chassis)",     key: "pressureFilter" },
  { label: "Grease outriggers with thick grease", customerLabel: "Outriggers greased",              key: "greaseOutriggers" },
];
const VERSALIFT_B = [
  ...VERSALIFT_A,
  { label: "Drain & replace hydraulic oil — ISO22 to 80% of sight gauge", customerLabel: "Hydraulic oil changed", key: "hydrOil", litres: "hydrLitres" },
];

const DONGHAE_A = [
  { label: "Take oil sample from main hydraulic tank",        key: "oilSample", hasPhoto: true },
  { label: "Replace suction filter (side of hyd tank)",        key: "suctionFilter" },
  { label: "Replace pressure filter (side of superstructure)", key: "pressureFilter" },
  { label: "Grease steel extension of boom (spray lube) + outrigger slides (thick grease)", customerLabel: "Boom & outriggers greased", key: "greaseBoom" },
];
const DONGHAE_B = [
  ...DONGHAE_A,
  { label: "Drain & replace hydraulic oil — ISO22 to 90% of sight gauge", customerLabel: "Hydraulic oil changed", key: "hydrOil", litres: "hydrLitres" },
  { label: "Drain & replace slew oil — 80W-90", customerLabel: "Slew oil changed",                key: "slewOil", litres: "slewLitres" },
  { label: "Ask customer if winch is used — if yes, drain & replace winch oil with 80W-90", customerLabel: "Winch oil changed (if fitted)", key: "winchOil", litres: "winchLitres" },
];

export const MODEL_SERVICE_ACTIONS = {
  "vacuum:av7000:A": AV7000_A,     "vacuum:av7000:B": AV7000_B,
  "vacuum:av2000:A": AV2000_A,     "vacuum:av2000:B": AV2000_B,
  "vacuum:av850:A": AV2000_A,      "vacuum:av850:B": AV2000_B,
  "vacuum:av6500:A": AV6500_A,     "vacuum:av6500:B": AV6500_B,
  "vacuum:av10000:A": AV10000_A,   "vacuum:av10000:B": AV10000_B,

  "hydrovac:av7000:A": AV7000_A,   "hydrovac:av7000:B": AV7000_B,
  "hydrovac:av2000:A": AV2000_A,   "hydrovac:av2000:B": AV2000_B,
  "hydrovac:av850:A": AV2000_A,    "hydrovac:av850:B": AV2000_B,
  "hydrovac:av6500:A": AV6500_A,   "hydrovac:av6500:B": AV6500_B,
  "hydrovac:av10000:A": AV10000_A, "hydrovac:av10000:B": AV10000_B,

  "crane:pk:A": PK_A,              "crane:pk:B": PK_B,

  "ewp:versalift:A": VERSALIFT_A,  "ewp:versalift:B": VERSALIFT_B,
  "ewp:donghae:A": DONGHAE_A,      "ewp:donghae:B": DONGHAE_B,
};

// ─── Model-specific inspection section overrides ─────────────────────────────
// Inspection sections normally come from the checksheet TEMPLATE (Vacuum vs
// Hydrovac), not the model — so a model-specific inspection item (like AV2000's
// engine checks) needs its own override keyed by model id, appended as an extra
// section on top of whatever the template already shows. Both AV850 and AV2000H
// share these items (same engine), independent of which template each uses.
const AV2000_ENGINE_ITEMS = [
  "Engine coolant — level & condition",
  "Drive belt(s) — tension & condition",
  "Gasket leaks — visual check (rocker cover, sump, etc.)",
  "Radiator — condition & leaks",
  "Battery — condition & terminals",
];

export const MODEL_SECTION_EXTRAS = {
  av850:  [{ id: "engine", title: "Engine Checks", items: AV2000_ENGINE_ITEMS }],
  av2000: [{ id: "engine", title: "Engine Checks", items: AV2000_ENGINE_ITEMS }],
};

// Every section id that ANY model-specific extra could introduce — used to clean
// up state when a technician switches models mid-sheet, so an extra section that
// no longer applies doesn't linger with stale answers.
export const ALL_MODEL_SECTION_EXTRA_IDS = Array.from(
  new Set(Object.values(MODEL_SECTION_EXTRAS).flatMap(list => list.map(s => s.id)))
);

// Returns the full section list to render for a given config + selected model:
// the template's own sections, plus any model-specific extra sections appended.
export function getSectionsForModel(configKey, modelId) {
  const config = CONFIGS[configKey];
  if (!config) return [];
  modelId = canonicalModelId(modelId);
  if (["PH","PR","PO"].includes(config.service)) return config.sections; // pre-hire / pre- & post-rebuild: own lists, no model extras
  const extras = modelId ? (MODEL_SECTION_EXTRAS[modelId] || []) : [];
  return extras.length ? [...config.sections, ...extras] : config.sections;
}

// ─── Combi (draft) — AV7000 Hydrovac sheet + a dedicated Jetting System section ──
// Uses the standard Hydrovac inspection checklist unchanged, plus a new
// "Jetting System" section for the jetting-specific checks. Flagged as draft —
// the jetting item list is a starting point per Waimea, more may be added.
// Combi has two hose reels — the small water-blaster reel (inherited from the
// Hydrovac functional list) and the large jetting reel (new section below).
// Rename just the Hydrovac line for clarity, without touching the shared array
// (pure Hydrovac trucks only have the one reel, so it stays generic there).
const COMBI_FUNCTIONAL_ITEMS = HV_FUNCTIONAL_ITEMS.map(item =>
  item === "Hose reel" ? "Hose reel (water blaster — small hose)" : item
);

CONFIGS["combi-a"] = {
  label: "Combi — A-Service (draft)", equipment: "Combi", service: "A", draft: true,
  equipmentFields: ["tankerModel", "tankerSerial"],
  vehicleFields: ["rego", "pto", "nextsvc"],
  certs: VAC_CERTS,
  sections: [
    { id: "cab",        title: "Visual Inspection — Cab",            items: CAB_ITEMS },
    { id: "chassis",    title: "Visual Inspection — Chassis & Deck", items: HV_CHASSIS_ITEMS },
    { id: "functional", title: "Functional Test",                    items: COMBI_FUNCTIONAL_ITEMS },
    { id: "jetting",    title: "Jetting System — DO NOT start pumps", items: [
        "Jetter unloader — visual condition (do NOT operate)",
        "Jetter pump — visual condition (do NOT operate)",
        "Jetting hose reel (large hose)",
        "Jetting hose — check layering on reel",
        "Jetting hose — check for nicks / damage",
        "Nozzles — condition",
      ] },
  ],
  operationalChecks: true,
  serviceActions: AV8000C_A,
};
CONFIGS["combi-b"] = {
  ...CONFIGS["combi-a"],
  label: "Combi — B-Service (draft)", service: "B",
  serviceActions: AV8000C_B,
};

// ─── Jetta — water tank + two hose reels (no vacuum/suction system) ─────────
// DRAFT: first pass built from the standard crossover checks (cab, chassis,
// hydraulic leaks — same as the other tanker units) plus the water tank /
// hose reel items specific to this unit. Flag for review.

const JETTA_CHASSIS_ITEMS = [
  "Hydraulic hosing — leaks",
  "Hydraulic hosing — condition & security",
  "Mudflaps — condition & security",
  "Chassis mounting — torque sealed",
  "Wheel chocks — condition & security",
  "Reflectors",
  "Cone holder",
  "Handrails — condition, security, compliance",
  "Toolbox condition",
  "PTO and pump",
  "Decals / load charts",
  "Level indicator",
  "Rust / corrosion — overall condition of deck & chassis",
  "Work lights & flashers",
];

const JETTA_WATER_SYSTEM_ITEMS = [
  "Water tank level & condition",
  "Water tank — leaks",
  "Water tank — check for cracks",
  "Water tank — check for contamination",
  "Tank sight glass — condition",
  "Jetter pump — visual condition (do NOT operate)",
  "Jetter pump — oil level & condition",
  "Hose reel 1 (large) — deploy / retract",
  "Hose reel 1 — hose condition (layering, nicks, damage)",
  "Hose reel 2 (small) — deploy / retract",
  "Hose reel 2 — hose condition (layering, nicks, damage)",
  "Nozzles — condition",
  "Unloader valve / safety valve / pump valves — condition & no visible leaks (inspect only — see note)",
];

// Functional checks done WITHOUT starting the pumps — pump start-up is treated
// as a dangerous operation on the Jetta and is excluded from this check.
const JETTA_OPERATIONAL_ITEMS = [
  "Hydraulic arm — raise / lower operation",
  "Hydraulic arm — extend / retract operation",
  "Turntable — rotation operation",
  "Turntable — locks correctly in position",
  "Hose reel — free spool (pay in / pay out)",
  "Hose reel — swivel ring condition & operation",
];

// NOTE: these were carried over from the Vacuum/Hydrovac functional list, but
// several of them (safety relief, 3-way valve, pressure relief, operating temp,
// gauges) normally require the pump running to actually verify — which conflicts
// with "DO NOT start pumps" above. Left in as static/visual checks for now
// pending your call — see the flagged list.
const JETTA_FUNCTIONAL_ITEMS = [
  "Safety relief valve — visual condition (do NOT operate)",
  "3-way valve — visual condition (do NOT operate)",
  "Pressure relief valve — visual condition (do NOT operate)",
  "Hydraulic oil level & condition",
  "Gauges — visual condition (do NOT operate)",
];

// Waimea visits every 6 months regardless of hour count — since that's the only
// touchpoint, the manufacturers' Daily/Weekly items get folded into every visit
// (A-Service) rather than skipped. Oils only happen at B-Service, matching every
// other equipment type's A/B convention. Per Waimea: the unloader/safety/pump
// valves are NOT replaced on a schedule — only if a tech finds one actually
// faulty, via the normal defect/repair process (see JETTA_WATER_SYSTEM_ITEMS).
const JETTA_SERVICE_A = [
  { label: "Check & clean water filter(s)",                              key: "waterFilterCheck" },
  { label: "Grease hose reel bearings",                                  key: "greaseHoseReelBearings" },
  { label: "Grease tail shaft / PTO universal joints",                   key: "greaseTailShaft" },
  { label: "Grease PTO splines (high-temp anti-fretting grease)", customerLabel: "PTO splines greased",        key: "greasePtoSplines" },
  { label: "Grease jetting pump safety-valve seals (waterproof grease)", customerLabel: "Safety-valve seals greased", key: "greaseSafetyValveSeals" },
  { label: "Reapply main reel shaft corrosion inhibitor (lanolin / fish-oil type)", customerLabel: "Corrosion inhibitor reapplied", key: "corrosionInhibitor" },
  { label: "Drain auxiliary air tank",                                   key: "drainAirTank" },
  { label: "Inspect pneumatic fittings, solenoids & silencers — contamination, leakage, damage", customerLabel: "Pneumatic system inspected", key: "pneumaticInspect" },
  { label: "Inspect high-pressure hoses (main, side, 5m leader) — damage & condition", customerLabel: "High-pressure hoses inspected", key: "hoseInspect" },
];
const JETTA_SERVICE_B = [
  ...JETTA_SERVICE_A,
  { label: "Change water pump oil — WM6000: SAE 85W-90 gear oil (~9L) / Ranger: SAE 15W-40", customerLabel: "Water pump oil changed", key: "waterPumpOilChange", litres: "waterPumpOilLitres" },
  { label: "Change hydraulic oil — Superdraulic / ISO VG46", customerLabel: "Hydraulic oil changed",             key: "hydrOilChange", litres: "hydrOilLitres" },
  { label: "Replace hydraulic oil filter",                               key: "hydrOilFilter" },
];

CONFIGS["jetta-a"] = {
  label: "Jetta — A-Service (draft)", equipment: "Jetta", service: "A", draft: true,
  equipmentFields: ["tankerModel", "tankerSerial"],
  vehicleFields: ["rego", "pto", "nextsvc"],
  certs: VAC_CERTS,
  sections: [
    { id: "cab",         title: "Visual Inspection — Cab",            items: CAB_ITEMS },
    { id: "chassis",     title: "Visual Inspection — Chassis & Deck", items: JETTA_CHASSIS_ITEMS },
    { id: "water",       title: "Water Tank & Hose Reels",            items: JETTA_WATER_SYSTEM_ITEMS },
    { id: "operational", title: "Operational Checks — DO NOT start pumps", items: JETTA_OPERATIONAL_ITEMS },
    { id: "functional",  title: "Functional Test",                    items: JETTA_FUNCTIONAL_ITEMS },
  ],
  serviceActions: JETTA_SERVICE_A,
};
CONFIGS["jetta-b"] = {
  ...CONFIGS["jetta-a"],
  label: "Jetta — B-Service (draft)", service: "B",
  serviceActions: JETTA_SERVICE_B,
};

// configKey like "vacuum-a" -> group "vacuum". Handles the one multi-word
// equipment id ("traffic-truck") by stripping only the trailing -a/-b.
export function getModelGroup(configKey) {
  return configKey ? configKey.replace(/-(a|b|oh|ph|pr|po)$/i, "") : null;
}

// Returns the serviceActions array to actually use for a given config + selected model.
// Falls back to the config's generic list if no model chosen or no override exists.
export function getServiceActionsForModel(configKey, modelId) {
  const config = CONFIGS[configKey];
  if (!config) return [];
  if (!modelId) return config.serviceActions;
  modelId = canonicalModelId(modelId);
  const group = getModelGroup(configKey);
  const service = config.service; // "A" | "B" | "OH" | "PH"
  if (["PH","PR","PO"].includes(service)) return config.serviceActions;
  if (service === "OH") {
    const modelA = MODEL_SERVICE_ACTIONS[`${group}:${modelId}:A`];
    return modelA ? offhireActions(modelA, group) : config.serviceActions;
  }
  const key = `${group}:${modelId}:${service}`;
  return MODEL_SERVICE_ACTIONS[key] || config.serviceActions;
}

// Equipment drawings on the home screen live in ./blueprints
export const EQUIPMENT_TYPES = [
  { id: "ewp",           label: "EWP",             Icon: EwpIcon },
  { id: "crane",         label: "Crane",            Icon: CraneIcon },
  { id: "vacuum",        label: "Vacuum",           Icon: VacuumIcon },
  { id: "combi",         label: "Combi",            Icon: CombiIcon },
  { id: "jetta",         label: "Jetta",            Icon: JettaIcon },
  { id: "traffic-truck", label: "Traffic Truck",    Icon: TrafficTruckIcon },
];

// ─── Off-hire checksheets ────────────────────────────────────────────────────
// Rental units coming back off hire: same visual inspection + functional test
// as the A-Service sheet for that equipment/model (incl. model extras such as
// the AV2000 engine checks). Service actions are the A-Service list for that
// equipment/model with every replacement removed — no filter or oil changes,
// no oil samples — leaving the checks, cleaning, level checks and the model's
// own greasing items. Every off-hire sheet also gets "Fill with diesel" + litres.
export const DIESEL_ACTION = { label: "Fill with diesel — record litres added", customerLabel: "Refuelled with diesel", key: "dieselFill", litres: "dieselLitres", showLitres: true };

export function isOffhireAction(item) {
  if (/do not replace/i.test(item.label)) return true;          // e.g. dust out air filter
  return !/replace|drain|change|oil sample/i.test(item.label);  // keep checks, cleaning, greasing
}
const TYRE_SHINE = { label: "Apply tyre shine to all tyres", customerLabel: "Tyres shined", key: "tyreShine" };
// Off-hire cleaning — done before the unit goes back out on hire.
const OFFHIRE_CLEANING_COMMON = [
  { label: "Clean cab interior — vacuum, wipe down, remove rubbish", customerLabel: "Cab interior cleaned", key: "cleanCab" },
  { label: "Clean out toolboxes", customerLabel: "Toolboxes cleaned out", key: "cleanToolboxes" },
  { label: "Wash superstructure & cab exterior", customerLabel: "Superstructure & cab exterior washed", key: "washSuper" },
  TYRE_SHINE,
];
const OFFHIRE_CLEANING_BY_GROUP = {
  "vacuum":   [{ label: "Wash out debris tank", customerLabel: "Debris tank washed out", key: "washTank" }],
  "hydrovac": [{ label: "Wash out debris tank", customerLabel: "Debris tank washed out", key: "washTank" }],
  "combi":    [{ label: "Wash out debris tank", customerLabel: "Debris tank washed out", key: "washTank" }],
  "jetta":    [{ label: "Flush & clean water tank", customerLabel: "Water tank flushed & cleaned", key: "flushTank" }],
};
// Condition photos on off-hire / pre-hire sheets — a record of how the unit left
// us, so there's no dispute later about what was dirty when it went out to the
// customer. Every unit gets the cab (except the AV850, which has none); the
// vacuum family also gets filters, sight glasses and the hopper/tank. Each slot
// takes one or more photos.
const HIRE_PHOTO_CAB = { key: "cab", label: "Cab interior — clean" };
const HIRE_PHOTOS_VACUUM = [
  { key: "waterFilter",  label: "Water filter" },
  { key: "finalFilter",  label: "Final filter" },
  { key: "sightGlasses", label: "Sight glasses — clean" },
  { key: "hopperTank",   label: "Hopper / tank — clean" },
  HIRE_PHOTO_CAB,
];
const HIRE_PHOTOS_BY_GROUP = { "vacuum": HIRE_PHOTOS_VACUUM, "hydrovac": HIRE_PHOTOS_VACUUM, "combi": HIRE_PHOTOS_VACUUM };
const NO_CAB_MODELS = new Set(["av850"]);
function hirePhotos(group) { return HIRE_PHOTOS_BY_GROUP[group] || [HIRE_PHOTO_CAB]; }
export function getHirePhotosForModel(configKey, modelId) {
  const slots = CONFIGS[configKey]?.hirePhotos || [];
  return modelId && NO_CAB_MODELS.has(canonicalModelId(modelId)) ? slots.filter(s => s.key !== "cab") : slots;
}
// Condition photos are saved as a list per slot (older sheets stored a single photo)
export function hirePhotoList(state, key) {
  const x = state.hirePhotos?.[key];
  return Array.isArray(x) ? x : x ? [x] : [];
}
export function offhireActions(aServiceList, group) {
  return [
    ...(aServiceList || []).filter(isOffhireAction),
    ...OFFHIRE_CLEANING_COMMON,
    ...(OFFHIRE_CLEANING_BY_GROUP[group] || []),
    DIESEL_ACTION,
  ];
}

// Pre-hire / hire-return checks (merged from the paper PRE HIRE INSPECTION
// CHECK SHEET). Items already covered by the A-Service inspection — operator
// manual, mudflaps, cab, reflectors, handrails, toolboxes, wheel chocks — are
// not repeated; diesel is a service action with litres. Equipment-specific
// lines only appear on the equipment they apply to.
// Rust/corrosion, foot (stabilizer) pads and the boom/crane functions are already
// checked in the Cab, Chassis and Functional Test sections, so they aren't
// repeated here. Damage photos are taken on the Fail itself, not as a check.
const HIRE_CHECKS_COMMON = [
  "Logbook present",
  "Interior — damage",
  "Interior — cleanliness",
  "Vehicle lights — head, tail, brake & indicators",
  "Wing mirrors — condition & security",
  "Body panel damage — deck, guards & toolboxes",
  "Alloy items polished — fuel tank, air tanks, toolboxes, guards & bumper",
  "Wheel nut indicators — OK (not faded or dull)",
];
const HIRE_CHECKS_BY_GROUP = {
  "ewp":      ["Fibreglass covers — condition", "Under-boom inspection for dents"],
  "crane":    ["Crane auxiliary tools — all present", "Under-boom inspection for dents"],
  "vacuum":   ["Suction hoses — present & condition", "Camlock handles — present"],
  "hydrovac": ["Suction hoses — present & condition", "Camlock handles — present"],
  "combi":    ["Suction hoses — present & condition", "Camlock handles — present"],
};
export function hireChecks(group) {
  // Tyres come last (not with the common checks) so answers on off-hire sheets
  // already in progress stay on their items — answers are kept by position.
  return [...HIRE_CHECKS_COMMON, ...(HIRE_CHECKS_BY_GROUP[group] || []), "Tyres — condition & pressures"];
}

// ─── Pre-hire checksheets ────────────────────────────────────────────────────
// For units that have been sitting (e.g. 6 months) and need a quick check before
// going out — not a full off-hire. Key checks taken from the off-hire sheet,
// refuel with litres, and one exterior wash. Same list for every unit.
export const PREHIRE_CHECKS = [
  "Starts & runs — no warning lights on the dash",
  "Battery — charged, terminals clean & tight",
  "Tyres — condition & pressures",
  "Wheel nut indicators — OK (not faded or dull)",
  "Fluid levels — engine oil, coolant & hydraulic oil",
  "No oil, fuel, coolant or hydraulic leaks",
  "Vehicle lights — head, tail, brake & indicators",
  "Rust & corrosion — none present on the unit",
  "Alloy items polished — fuel tank, air tanks, toolboxes, guards & bumper",
  "Superstructure — function test, all controls operate",
  "Logbook & operator manual present",
];  // rego & CoF are covered by the Certs section
export const PREHIRE_ACTIONS = [
  { label: "Wash superstructure & cab exterior", customerLabel: "Superstructure & cab exterior washed", key: "washSuper" },
  TYRE_SHINE,
];

Object.keys(CONFIGS).filter(k => /-a$/.test(k)).forEach(k => {
  const base = CONFIGS[k];
  const group = k.replace(/-a$/, "");
  CONFIGS[`${group}-ph`] = {
    ...base,
    label: base.label.replace(/A-Service/, "Pre-hire"),
    service: "PH",
    equipmentFields: (base.equipmentFields || []).filter(f => f !== "serial" && f !== "tankerSerial"),
    vehicleFields: ["rego", "odo", "enghours", "pto"],
    sections: [{ id: "prehire", title: "Pre-hire Checks", items: PREHIRE_CHECKS }],
    serviceActions: [...PREHIRE_ACTIONS, DIESEL_ACTION],
    hirePhotos: hirePhotos(group),
    operationalChecks: false,
  };
});

Object.keys(CONFIGS).filter(k => /-a$/.test(k)).forEach(k => {
  const base = CONFIGS[k];
  const group = k.replace(/-a$/, "");
  const ohKey = `${group}-oh`;
  CONFIGS[ohKey] = {
    ...base,
    label: base.label.replace(/A-Service/, "Off-hire"),
    service: "OH",
    vehicleFields: ["rego", "odo", "enghours", "pto", "nextsvc"],
    equipmentFields: (base.equipmentFields || []).filter(f => f !== "serial" && f !== "tankerSerial"),
    sections: [{ id: "hire", title: "Hire Return Checks", items: hireChecks(group) }, ...base.sections],
    serviceActions: offhireActions(base.serviceActions, group),
    hirePhotos: hirePhotos(group),
  };
});

// Display name for a config's service level: "A-Service", "B-Service", "Off-hire".
export function serviceLabel(configOrService) {
  const s = typeof configOrService === "string" ? configOrService : configOrService?.service;
  if (s === "OH") return "Off-hire";
  if (s === "PH") return "Pre-hire";
  if (s === "PR") return "Pre-rebuild";
  if (s === "PO") return "Post-rebuild";
  return s ? `${s}-Service` : "";
}
// Badge colour per service level.
export function serviceColor(configOrService) {
  const s = typeof configOrService === "string" ? configOrService : configOrService?.service;
  return s === "A" ? "#E8A020" : s === "OH" ? "#7C3AED" : s === "PH" ? "#0E9F6E" : s === "PR" ? "#B42318" : s === "PO" ? "#0F766E" : "#2BACD1";
}


// ─── EWP Pre-rebuild inspection (Compliance Pre-Inspection Report) ───────────
// Full inspection of an EWP before a rebuild is quoted: visual checks, functional
// test with each direction tested separately, cycle times, PTO pump test and
// deflection test (with photos), certs, and a team leader sign-off. No service
// actions — it's an inspection only. EWP only.
const PR_CAB_ITEMS = [  // cert labels are checked (with expiry) in the Certs section
  "Decals — travel height",
  "Decals — switches labelled",
  "Operators manual",
  "Damage / condition",
  "Rust / corrosion — overall condition of cab",
];
const PR_CHASSIS_ITEMS = [
  "Hydraulic hosing — leaks",
  "Hydraulic hosing — condition & security",
  "Outriggers / jacks — condition & cleanliness",
  "Wheel nut indicators",
  "Cone holder",
  "Handrails — condition, security, compliance",
  "Mudflaps — condition & security",
  "Chassis mounting — torque sealed",
  "Foot pads — condition & security",
  "Wheel chocks — condition & security",
  "Reflectors",
  "Earth stud",
  "Toolbox condition",
  "Toolboxes — latches & hinges",
  "Spare remote battery — charging & security",
  "Decals / load charts",
  "Lower control decals",
  "Level indicator",
  "Rust / corrosion — overall condition of deck & chassis",
  "Work lights",
  "Tyres — condition & pressures",   // added last so answers on sheets in progress stay on their items
];
const PR_BOOM_ITEMS = [
  "Decals",
  "Boom certification sticker",
  "Covers — condition & security",
  "Home stow position indicators",
  "Winch / rope",
  "Upper control decals",
  "Bucket & boom rest condition",
  "Liner condition",
  "Slew bolts — torque sealed",
  "Pins",
  "Critical welds — cracks",
  "Bucket access — safe & secure",
  "Bucket & winch covers",
  "Rust / corrosion — overall condition of boom & bucket",
];
const PR_FUNCTIONAL_ITEMS = [
  "Jacks & feet",
  "Outriggers & spring locks",
  "Interlock — no boom operation unless jacks set",
  "Interlock — no jack operation unless boom stowed",
  "Boom raise / lower",
  "Boom extend / retract",
  "Boom rotation — CW & CCW",
  "Bucket raise / lower",
  "Bucket rotation — CW & CCW",
  "Arm rotation — CW & CCW",
  "Winch raise / lower",
  "Winch pay in / out",
  "Winch rotation — CW & CCW",
  "Tool port operation",
  "Bucket tilt / level adjustment",
  "Jib",
  "Wire ropes",
  "Wear pads",
  "Flashers",
  "E-pump",
  "Overrides",
  "Oil level & condition",
  "Pressure filter",
  "Return filter",
  "Auto storage",
  "Cab collision program",
  "Handrail collision program",
  "Hydraulic cooling fan — operation",   // added last so sheets in progress keep their answers in place
  "Deadman switch — upper (platform) controls: fitted & working",
  "Deadman switch — lower (ground) controls: fitted & working",
];
// Readings — numbers, with N/A where a unit doesn't have that movement.
export const PR_MEASUREMENTS = [
  { id: "cycle", title: "Cycle Times", targets: true, note: "Time each movement in seconds, and enter the target min / max from the manual. Use N/A if the unit doesn't have it.", items: [
    { key: "ctRotCW",    label: "Boom rotation CW",         unit: "sec", allowNA: true },
    { key: "ctRotCCW",   label: "Boom rotation CCW",        unit: "sec", allowNA: true },
    { key: "ct1Raise",   label: "First boom raise",         unit: "sec", allowNA: true },
    { key: "ct1Lower",   label: "First boom lower",         unit: "sec", allowNA: true },
    { key: "ct2Raise",   label: "Second boom raise",        unit: "sec", allowNA: true },
    { key: "ct2Lower",   label: "Second boom lower",        unit: "sec", allowNA: true },
    { key: "ctTelExt",   label: "Telescopic boom extend",   unit: "sec", allowNA: true },
    { key: "ctTelRet",   label: "Telescopic boom retract",  unit: "sec", allowNA: true },
  ]},
  { id: "pump", title: "PTO Pump Test", targets: "recommended", note: "Enter the recommended figure from the manual for each; readings within ±5% of it are in range.", items: [
    { key: "pumpPsi",  label: "Pump pressure", unit: "psi" },
    { key: "pumpFlow", label: "Pump flow",     unit: "L/min" },
  ], photos: { label: "Photos of pump & PTO test", required: true } },
  { id: "deflection", title: "Deflection Test", items: [
    { key: "deflection", label: "Deflection noticed", unit: "mm" },
  ], photos: { label: "Photos of deflection test", required: true } },
];

CONFIGS["ewp-pr"] = {
  label: "EWP — Pre-rebuild", equipment: "EWP", service: "PR",
  equipmentFields: ["make", "model", "serial"],
  vehicleFields: ["rego", "odo", "pto", "nextsvc"],
  certs: EWP_CERTS,
  sections: [
    { id: "cab",        title: "Visual Inspection — Cab",           items: PR_CAB_ITEMS },
    { id: "chassis",    title: "Visual Inspection — Chassis & Deck", items: PR_CHASSIS_ITEMS },
    { id: "boom",       title: "Visual Inspection — Boom & Bucket",  items: PR_BOOM_ITEMS },
    { id: "functional", title: "Functional Test",                    items: PR_FUNCTIONAL_ITEMS },
  ],
  serviceActions: [],
  measurements: PR_MEASUREMENTS,
  teamLeaderSignoff: true,
  // A rebuild can keep the unit here for up to 3 months, so a cert expiring in
  // that time is flagged Due and added to Defects, to be offered on the quote.
  // Registration, the EWP 6-monthly and the major inspection are excluded
  // (normal 1-month Due, no defect line).
  certOffer: { months: 3, except: ["registration", "ewp6m", "major5y"] },
};

// ─── EWP Post-rebuild inspection ─────────────────────────────────────────────
// The same checks and tests as the pre-rebuild, done after the rebuild. The
// pre-rebuild results can be imported (from its PDF, or a sheet still on the
// device) and are shown greyed out beside each item for comparison; cycle
// times / pump / deflection readings that changed by more than 10% are flagged.
CONFIGS["ewp-po"] = {
  ...CONFIGS["ewp-pr"],
  label: "EWP — Post-rebuild", service: "PO",
  comparesWith: "PR",
  certOffer: null,   // cert renewals are offered on the pre-rebuild quote
};

// Pre-rebuild: does this cert get the longer Due window and a defect line?
export function certOfferFor(config, certId) {
  const o = config && config.certOffer;
  return o && !o.except.includes(certId) ? o : null;
}
export const REBUILD_FLAG_PCT = 10;
// Targets from the manual are stored beside the reading. Cycle times
// (group.targets === true) have a range: measures["<key>_min"] / ["<key>_max"].
// The PTO pump test (group.targets === "recommended") has one recommended figure,
// measures["<key>_rec"], and is in range within ±REC_TOLERANCE_PCT of it.
export const REC_TOLERANCE_PCT = 5;
const round1 = n => Math.round(n * 10) / 10;
// Returns { min, max } (plus rec for a recommended figure) or null
export function targetRange(measures, key) {
  const a = parseFloat(measures?.[`${key}_min`]), b = parseFloat(measures?.[`${key}_max`]);
  if (isFinite(a) && isFinite(b)) return { min: Math.min(a, b), max: Math.max(a, b) };
  const rec = parseFloat(measures?.[`${key}_rec`]);
  if (isFinite(rec) && rec > 0) return { rec, min: round1(rec * (1 - REC_TOLERANCE_PCT / 100)), max: round1(rec * (1 + REC_TOLERANCE_PCT / 100)) };
  return null;
}
// "60–75 sec" or "3000 psi ±5% (2850–3150)"
export function targetText(range, unit) {
  if (!range) return "";
  return range.rec != null ? `${range.rec} ${unit} ±${REC_TOLERANCE_PCT}% (${range.min}–${range.max})` : `${range.min}–${range.max} ${unit}`;
}
// A reading against its target range: "low" | "ok" | "high", or null if either is missing
export function rangeStatus(val, range) {
  const v = parseFloat(val);
  if (!range || val === "na" || !isFinite(v)) return null;
  return v < range.min ? "low" : v > range.max ? "high" : "ok";
}
// Change between a before and after reading: { diff, pct, flag } or null
export function readingChange(before, after) {
  const b = parseFloat(before), a = parseFloat(after);
  if (!isFinite(b) || !isFinite(a) || before === "na" || after === "na") return null;
  const diff = a - b;
  const pct = b === 0 ? (a === 0 ? 0 : Infinity) : (diff / b) * 100;
  return { diff, pct, flag: Math.abs(pct) > REBUILD_FLAG_PCT };
}
// Pre-rebuild results as carried in its PDF / used for comparison (no photos)
export function rebuildResults(config, header, state) {
  const sections = {};
  (config.sections || []).forEach(sec => {
    sections[sec.id] = sec.items.map((label, i) => {
      const it = state[sec.id]?.[i] || {};
      return { label, status: it.status || null, note: [it.note, ...(it.extraFaults || []).map(f => f.note)].filter(Boolean).join("; ") };
    });
  });
  const certs = {};
  (config.certs || []).forEach(c => { const cur = state.certs?.[c.id] || {}; certs[c.id] = { label: c.label, val: cur.val ?? null, expiry: cur.expiry || "" }; });
  return {
    jobno: header.jobno || "", date: header.date || "", tech: header.tech || "",
    sections, certs,
    measures: { ...(state.measures || {}) },
    defects: (state.defects || []).filter(d => d.defect).map(d => d.defect),
  };
}
